/**
 * Who follows a conversation — Aaron, Oct 26: a teammate picked into an
 * email's Cc or Bcc has the thread in their Inbox too, is rung for the
 * customer's replies like its owner, and can be taken off it again.
 *
 * On a desktop it is a line in the thread's head under *Attached to* — a face
 * and a name per person, × to take them off; on a phone, a row of the
 * thread's ⋯ sheet that opens the list as its own sheet (§10.2: 52 px rows).
 * Anybody who can open the conversation may take somebody off it, the
 * follower included (*Stop following*); somebody who follows it only to have
 * it then no longer has it, and goes back to their list.
 */
import { useState } from 'react'
import type { ConversationThread, ThreadFollower } from '@rowted/shared'
import { PanelSheet } from '../../components/panel-sheet.js'
import { cn } from '../../lib/cn.js'
import { activityRequest } from '../api.js'
import { useActivity } from '../activity-context.js'
import { BackLayer } from '../use-activity-history.js'
import { Face } from '../faces.js'
import { Glyph } from '../glyphs.js'
import { errorWords } from './look.js'

/** *Added by Tom Reyes · Cc* — what a follower's title and a phone row say about them. */
export function followerWords(follower: ThreadFollower): string {
  const box = follower.copiedAs === 'bcc' ? 'Bcc' : 'Cc'
  return follower.addedBy ? `added by ${follower.addedBy} · ${box}` : box
}

function useUnfollow(thread: ConversationThread, onChanged: () => void, onLeft: () => void) {
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const remove = async (userId: string) => {
    setBusy(userId)
    setError(null)
    try {
      const answer = await activityRequest<{ stillOpen: boolean }>(
        `/conversations/${thread.id}/followers/${userId}/remove`, { method: 'POST', body: {} })
      if (answer.stillOpen) onChanged()
      else onLeft()
    } catch (cause) {
      setError(errorWords(cause))
    } finally {
      setBusy(null)
    }
  }
  return { remove, error, busy }
}

/** The head's *Following* line (desktop and iPad). Nothing at all while nobody follows it. */
export function FollowingLine({
  thread, onChanged, onLeft,
}: { thread: ConversationThread; onChanged: () => void; onLeft: () => void }) {
  const { me } = useActivity()
  const { remove, error, busy } = useUnfollow(thread, onChanged, onLeft)
  if (thread.followers.length === 0) return null
  return (
    <>
      <div className="h-1.5" />
      <div className="flex flex-wrap items-center gap-1.5 text-[11.5px] text-text-muted" aria-label="Following">
        <span className="whitespace-nowrap">Following</span>
        {thread.followers.map((follower) => {
          const self = follower.id === me.user.id
          return (
            <span key={follower.id} data-follower title={followerWords(follower)}
              className="inline-flex h-[22px] items-center gap-1 whitespace-nowrap rounded-full border border-line bg-surface pl-[2px] pr-1.5 text-[11.5px] font-semibold text-text">
              <Face person={follower} size={18} self={self} />
              {self ? 'You' : follower.name}
              {follower.copiedAs === 'bcc' && <span className="font-medium text-text-faint">Bcc</span>}
              <button type="button" disabled={busy === follower.id} onClick={() => void remove(follower.id)}
                aria-label={self ? 'Stop following' : `Take ${follower.name} off it`}
                className="inline-flex text-text-muted hover:text-text disabled:opacity-50">
                <Glyph name="close" size={11} stroke={2.4} />
              </button>
            </span>
          )
        })}
      </div>
      {error && <div className="mt-1 text-[12px] text-danger">{error}</div>}
    </>
  )
}

/** The phone's: the people following it, each with *Remove* — *Stop following* on your own row. */
export function FollowersSheet({
  thread, open, onClose, onChanged, onLeft,
}: { thread: ConversationThread; open: boolean; onClose: () => void; onChanged: () => void; onLeft: () => void }) {
  const { me } = useActivity()
  const { remove, error, busy } = useUnfollow(thread, () => { onChanged() }, () => { onClose(); onLeft() })
  return (
    <>
    {/* Back closes the sheet before the thread under it (Aaron, Oct 26). */}
    {open && <BackLayer onBack={onClose} />}
    <PanelSheet open={open} onClose={onClose} title="Following" sub={thread.row.name} layer={70}>
      {thread.followers.map((follower) => {
        const self = follower.id === me.user.id
        return (
          <div key={follower.id} data-follower className="flex min-h-[52px] items-center gap-3 border-b border-line-faint px-4">
            <Face person={follower} size={26} self={self} />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-[15px] font-medium">{self ? `${follower.name} (you)` : follower.name}</span>
              <span className="truncate text-[12.5px] text-text-muted">{followerWords(follower)}</span>
            </span>
            <button type="button" disabled={busy === follower.id} onClick={() => void remove(follower.id)}
              className={cn('inline-flex h-11 shrink-0 items-center px-2 text-[14px] font-semibold disabled:opacity-50',
                self ? 'text-danger' : 'text-accent')}>
              {self ? 'Stop following' : 'Remove'}
            </button>
          </div>
        )
      })}
      {thread.followers.length === 0 && (
        <div className="px-4 py-4 text-[14px] text-text-muted">Nobody else follows it.</div>
      )}
      {error && <div className="px-4 py-3 text-[13px] text-danger">{error}</div>}
    </PanelSheet>
    </>
  )
}
